import { cn } from '@/lib/utils';
import { Icon } from '@chiroup/components/Icon';
import { Icons } from '@chiroup/components/icons';
import { ImprovementOptions } from '@chiroup/core/types/PatientVisit.type';
import { useWindowDimensions } from '@chiroup/hooks/useWindowDimensions';
import { RadioGroup } from '@headlessui/react';
import { ArrowRightCircle as ArrowRightCircleIcon, Minus as MinusIcon } from 'lucide-react';

const options = [
  {
    label: 'Significantly worse',
    bgColor: 'bg-red-600',
    ringColor: 'ring-red-600',
    icon: (
      <div className="flex flex-row">
        <MinusIcon className="w-4" />
        <MinusIcon className="w-4" />
      </div>
    ),
    value: ImprovementOptions.SignificantlyWorse,
  },
  {
    label: 'Worse',
    bgColor: 'bg-red-500',
    ringColor: 'ring-red-500',
    icon: <MinusIcon className="w-4" />,
    value: ImprovementOptions.Worse,
  },
  {
    label: 'The same',
    bgColor: 'bg-accent-500',
    ringColor: 'ring-accent-500',
    icon: (
      <div className="relative flex flex-col w-4 h-4">
        <MinusIcon className="absolute left-0 w-4 -top-1" />
        <MinusIcon className="absolute left-0 w-4 top-1" />
      </div>
    ),
    value: ImprovementOptions.Unchanged,
  },
  {
    label: 'Better',
    bgColor: 'bg-primary-400',
    ringColor: 'ring-primary-400',
    icon: <Icon icon={Icons.add} className="w-4" />,
    value: ImprovementOptions.Improved,
  },
  {
    label: 'Significantly better',
    bgColor: 'bg-primary-600',
    ringColor: 'ring-primary-600',
    icon: (
      <div className="flex flex-row">
        <Icon icon={Icons.add} className="w-4" />
        <Icon icon={Icons.add} className="w-4" />
      </div>
    ),
    value: ImprovementOptions.SignificantlyImproved,
  },
];

type Props = {
  title: string;
  detail?: () => void;
  detailDesktop?: () => void;
  detailSelected?: boolean;
  className?: string;
  value?: ImprovementOptions | null;
  onChange: (val: ImprovementOptions | null) => void;
  showOptions?: boolean;
  top?: boolean;
  bottom?: boolean;
  largeText?: boolean;
  isSaving?: boolean;
  disabled?: boolean;
};

const Improvement: React.FC<Props> = ({
  title,
  detail,
  detailDesktop,
  detailSelected,
  className = '',
  value,
  onChange,
  showOptions = false,
  top,
  bottom,
  largeText = false,
  isSaving = false,
  disabled = false,
}) => {
  const { width } = useWindowDimensions();

  return (
    <div
      className={cn(
        className,
        detailSelected ? 'bg-primary-50 dark:bg-dark-gray-700' : 'bg-transparent',
        top ? 'rounded-t-lg' : bottom ? 'rounded-b-lg' : ''
      )}
    >
      <div className="flex flex-col gap-2">
        <div
          className="flex-row items-center justify-between cursor-pointer hidden xl:flex p-2"
          onClick={detailDesktop}
        >
          <span
            className={cn(
              'block font-medium text-gray-700 dark:text-dark-gray-50',
              largeText ? 'text-md' : 'text-sm'
            )}
          >
            {title}
          </span>
          {!!detail && (
            <ArrowRightCircleIcon
              className={cn(
                'hidden xl:block w-4 h-4 cursor-pointer',
                detailSelected
                  ? 'text-primary-500 hover:text-primary-600'
                  : 'text-gray-400 hover:text-gray-600'
              )}
            />
          )}
        </div>
        <div
          className="flex flex-row items-center justify-between cursor-pointer xl:hidden p-2"
          onClick={detail}
        >
          <span
            className={cn(
              'block font-medium text-gray-700 dark:text-dark-gray-50',
              largeText ? 'text-md' : 'text-sm'
            )}
          >
            {title}
          </span>
          {!!detail && (
            <Icon
              icon={Icons.newWindow}
              className="block w-4 h-4 text-gray-400 cursor-pointer xl:hidden hover:text-gray-600"
            />
          )}
        </div>
        {showOptions && (
          <RadioGroup
            value={value ?? null}
            onChange={onChange}
            className="flex flex-row gap-6 w-full border-primary-600 p-2"
            disabled={disabled}
          >
            {options.map(option => (
              <RadioGroup.Option
                key={option.label}
                disabled={isSaving || disabled}
                value={option.value}
                className={({ active }) =>
                  cn(
                    option.ringColor,
                    active && value === option.value ? 'ring ring-offset-1 z-10' : '',
                    !active && value === option.value ? 'sm:ring-2 z-10' : '',
                    'relative rounded-full flex items-center justify-center cursor-pointer focus:outline-none'
                  )
                }
                onClick={() => {
                  if (disabled) return;

                  if (value === option.value) {
                    onChange(null);
                  }

                  if (width > 1280) {
                    detailDesktop?.();
                  } else {
                    detail?.();
                  }
                }}
              >
                <RadioGroup.Label as="span" className="sr-only">
                  {option.label}
                </RadioGroup.Label>
                <span
                  aria-hidden="true"
                  className={cn(
                    value === option.value
                      ? `${option.bgColor} text-white`
                      : 'bg-gray-300 dark:bg-dark-gray-600 text-gray-400',
                    'h-10 w-10 border border-gray-600/10 rounded-full flex items-center justify-center ring-2 ring-white dark:ring-dark-gray-500'
                  )}
                >
                  <div>{option.icon}</div>
                </span>
              </RadioGroup.Option>
            ))}
          </RadioGroup>
        )}
      </div>
    </div>
  );
};

export default Improvement;
